import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { BsThreeDotsVertical, BsChevronDown, BsChevronRight, BsGripVertical } from 'react-icons/bs';
import { FiHardDrive } from 'react-icons/fi';
import { IoPersonOutline } from 'react-icons/io5';
import { CiCalendar } from 'react-icons/ci';
import { type DraggableProvided } from 'react-beautiful-dnd';
import Select, { type SingleValue, type StylesConfig } from 'react-select';

import { getLectureSlideDeckDownloadUrl } from '@editor2/services/apiService';
import { type CourseFile, type SelectOption } from './types';

interface Props {
	file: CourseFile;
	courseId: number;
	selectOptions: SelectOption[];
	defaultExpanded?: boolean;
	onChange: (updated: CourseFile) => void;
	onRemove: (id: string) => void;
	onReplace: (id: string) => void;
	draggableProvided: DraggableProvided;
	isDragging: boolean;
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
	const d = new Date(iso);
	return d.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	});
}

const FileRow: FC<Props> = ({
	file,
	courseId,
	selectOptions,
	defaultExpanded = false,
	onChange,
	onRemove,
	onReplace,
	draggableProvided,
	isDragging
}) => {
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [copied, setCopied] = useState(false);
	const copiedTimer = useRef<ReturnType<typeof setTimeout>>();
	const isUploading = file.upload?.status === 'uploading';
	const uploadFailed = file.upload?.status === 'failed';

	// Auto-expand the row when an upload starts so the single progress indicator is visible.
	useEffect(() => {
		if (isUploading || uploadFailed) {
			setExpanded(true);
		}
	}, [isUploading, uploadFailed]);

	const handleDownload = async () => {
		if (!file.lectureSlideDeckFamilyId) return;
		try {
			const { data } = await getLectureSlideDeckDownloadUrl(
				courseId,
				file.lectureSlideDeckFamilyId
			);
			window.open(data.url, '_blank');
		} catch {
			// Silently fail — the link may not be available yet.
		}
	};

	const unassignedOption: SelectOption = useMemo(
		() => ({ value: '', label: '-- Unassigned --', type: 'page' as const }),
		[]
	);

	const allOptions = useMemo(
		() => [unassignedOption, ...selectOptions],
		[unassignedOption, selectOptions]
	);

	const selectedOption = useMemo(
		() => allOptions.find((opt) => opt.value === file.associatedFamilyId) ?? unassignedOption,
		[allOptions, file.associatedFamilyId, unassignedOption]
	);

	const handleSelectChange = (opt: SingleValue<SelectOption>) => {
		onChange({ ...file, associatedFamilyId: opt?.value ?? '' });
	};

	const selectStyles: StylesConfig<SelectOption, false> = useMemo(
		() => ({
			control: (base) => ({
				...base,
				minHeight: 31,
				fontSize: 13
			}),
			option: (base, state) => {
				const isNonNormalPage =
					state.data.type === 'page' && state.data.pageType && state.data.pageType !== 'normal';
				return {
					...base,
					fontSize: 13,
					fontWeight: isNonNormalPage ? 700 : state.data.type === 'chapter' ? 600 : 400,
					fontStyle: isNonNormalPage ? 'italic' : 'normal',
					paddingLeft: state.data.type === 'page' ? 24 : 12
				};
			},
			singleValue: (base) => {
				const isNonNormalPage =
					selectedOption.type === 'page' &&
					selectedOption.pageType &&
					selectedOption.pageType !== 'normal';
				return {
					...base,
					fontSize: 13,
					fontWeight: isNonNormalPage ? 700 : 400,
					fontStyle: isNonNormalPage ? 'italic' : 'normal'
				};
			},
			menu: (base) => ({
				...base,
				zIndex: 10
			})
		}),
		[selectedOption]
	);

	return (
		<>
			<tr
				className={`fileMainRow${isDragging ? ' isDragging' : ''}${expanded ? ' isExpanded' : ''}`}
				ref={draggableProvided.innerRef}
				{...draggableProvided.draggableProps}>
				<td className="dragHandleCell" {...draggableProvided.dragHandleProps}>
					<BsGripVertical aria-label="Drag to reorder" />
				</td>
				<td className="fileNameCell">
					<div className="fileNameContent">
						<button
							type="button"
							className="expandToggle"
							aria-expanded={expanded}
							aria-label={expanded ? 'Collapse file details' : 'Expand file details'}
							onClick={() => setExpanded((prev) => !prev)}>
							{expanded ? <BsChevronDown /> : <BsChevronRight />}
						</button>
						<button
							type="button"
							className="fileNameToggle"
							title={file.fileName}
							onClick={() => setExpanded((prev) => !prev)}>
							{file.fileName}
						</button>
					</div>
					<div className="responsiveChapterPage">
						<Select<SelectOption>
							options={allOptions}
							value={selectedOption}
							onChange={handleSelectChange}
							isDisabled={isUploading}
							isSearchable
							aria-label="Associate with chapter or page"
							styles={selectStyles}
							menuPortalTarget={document.body}
						/>
					</div>
				</td>

				<td className="chapterPageCell">
					<Select<SelectOption>
						options={allOptions}
						value={selectedOption}
						onChange={handleSelectChange}
						isDisabled={isUploading}
						isSearchable
						aria-label="Associate with chapter or page"
						styles={selectStyles}
						menuPortalTarget={document.body}
					/>
				</td>

				<td className="selfServeSampleCell">
					<Form.Check
						type="checkbox"
						aria-label="Self-serve sample"
						checked={file.isSelfServeSample}
						disabled={isUploading}
						onChange={(e) => onChange({ ...file, isSelfServeSample: e.target.checked })}
					/>
				</td>

				<td className="actionsCell">
					<Dropdown>
						<Dropdown.Toggle
							as="button"
							className="fileActionsToggle"
							aria-label="File actions"
							aria-haspopup="true"
							disabled={isUploading}>
							<BsThreeDotsVertical />
						</Dropdown.Toggle>
						<Dropdown.Menu alignRight popperConfig={{ strategy: 'fixed' }}>
							{file.lectureSlideDeckFamilyId && (
								<Dropdown.Item onClick={handleDownload}>Download</Dropdown.Item>
							)}
							<Dropdown.Item onClick={() => onReplace(file.id)}>Replace file</Dropdown.Item>
							<Dropdown.Item className="dangerItem" onClick={() => onRemove(file.id)}>
								Remove file
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</td>
			</tr>

			{expanded && !isDragging && (
				<tr className="fileDetailRow">
					<td colSpan={5}>
						{(isUploading || uploadFailed) && (
							<div className="uploadDetails">
								{isUploading && (
									<>
										<div
											className="rowProgressBar"
											role="progressbar"
											aria-valuenow={file.upload!.progress}
											aria-valuemin={0}
											aria-valuemax={100}>
											<div
												className="rowProgressFill"
												style={{ width: `${file.upload!.progress}%` }}
											/>
										</div>
										<span className="uploadProgressText">Uploading… {file.upload!.progress}%</span>
									</>
								)}
								{uploadFailed && (
									<span className="uploadErrorText">{file.upload!.error ?? 'Upload failed'}</span>
								)}
							</div>
						)}

						{!isUploading && !uploadFailed && (
							<div className="fileDetails">
								<div className="fileMetadata">
									{file.filesizeBytes != null && (
										<span className="metaItem">
											<FiHardDrive className="metaIcon" aria-hidden="true" /> Size:{' '}
											<strong>{formatFileSize(file.filesizeBytes)}</strong>
										</span>
									)}
									{file.uploaderName && (
										<span className="metaItem">
											<IoPersonOutline className="metaIcon" aria-hidden="true" /> Uploaded by{' '}
											<strong>{file.uploaderName}</strong>
										</span>
									)}
									{file.uploadedAt && (
										<span className="metaItem">
											<CiCalendar className="metaIcon" aria-hidden="true" /> Added on{' '}
											<strong>{formatDate(file.uploadedAt)}</strong>
										</span>
									)}
								</div>
								{file.lectureSlideDeckFamilyId && (
									<div className="deckLink">
										<code>{`soomo://lecture_slides/${file.lectureSlideDeckFamilyId}`}</code>
										<button
											type="button"
											className={`copyLinkButton${copied ? ' copied' : ''}`}
											aria-label="Copy lecture slide link"
											onClick={() => {
												void navigator.clipboard.writeText(
													`soomo://lecture_slides/${file.lectureSlideDeckFamilyId}`
												);
												clearTimeout(copiedTimer.current);
												setCopied(true);
												copiedTimer.current = setTimeout(() => setCopied(false), 1500);
											}}>
											{copied ? 'Copied!' : 'Copy'}
										</button>
									</div>
								)}
							</div>
						)}

						{/* Notes are always editable when available (e.g. during replacement uploads) */}
						{!isUploading && (
							<Form.Control
								as="textarea"
								rows={2}
								placeholder="Add notes about this file..."
								value={file.notes ?? ''}
								onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
									onChange({ ...file, notes: e.target.value })
								}
								className="fileNotesInput"
							/>
						)}
					</td>
				</tr>
			)}
		</>
	);
};

export default FileRow;
